/**
 * AGC: Interactive Tournament Battle Component
 * AGC: Real-time turn-based battle interface for Tournament matches
 * AGC: Based on InteractiveP2PBattle but reports results to tournament backend
 * AGC: Uses reusable components: BattleTop, BattleCenter, BattleLog
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Socket } from 'socket.io-client'
import { ethers } from 'ethers'
import { usePlayerProgression } from '../../hooks/usePlayerProgression'
import { usePlayerHistory } from '../../hooks/usePlayerHistory'
import { GameEngine } from '../../game/GameEngine'
import { playSoundSafe, unlockAudioPlayback } from '../../utils/audio'
import { BattleTurnModal, BattleTurnSnapshot } from '../modals/BattleTurnModal'
import { BattleTop, PlayerStats as BattleTopPlayerStats, OpponentInfo } from '../battle/BattleTop'
import { BattleCenter, Choice, Element } from '../battle/BattleCenter'
import { BattleLog, TurnResult as BattleLogTurnResult } from '../battle/BattleLog'

interface InteractiveTournamentBattleProps {
  battleId: string
  tournamentId: string
  playerAddress: string
  opponentAddress: string
  opponentLevel: number
  socket: Socket | null
  selectedBuffs: number[] // AGC: NFT buff IDs that player burned for this battle
  selectedPotion?: number | null // AGC: Potion token ID that player escrowed for this battle
  onBattleEnd: (won: boolean, turns: number, isDraw?: boolean, resolutionTxHash?: string | null) => void
  onBackToMenu: () => void
}

// AGC: Choice and Element types are now imported from BattleCenter

type BuffStatus = 'activated' | 'failed' | 'disabled' | 'available' | string

interface BuffStatusEntry {
  id: number
  name: string
  status: BuffStatus
}

interface DamageBreakdown {
  baseName: string
  baseDamage: number
  levelBonus: number
  elementBonus: number
  elementName: string
  rarityBonus: number
  rarityName: string
  hasteBonus: number
  rawDamage: number
  defense: number
  afterDefense: number
  pierceReduction?: number
  shieldReduction?: number
  frostReduction?: number
}

interface TurnResult {
  turnNumber: number
  yourDamage: number
  opponentDamage: number
  yourEnergy: number
  opponentEnergy: number
  yourChoice: Choice
  opponentChoice: Choice
  yourElement: Element
  opponentElement: Element
  yourBuffs?: number[] // AGC: Buff IDs used by player
  opponentBuffs?: number[] // AGC: Buff IDs used by opponent
  yourActivatedBuff?: string | null // AGC: Which buff proc'd (SHIELD, PIERCE, etc.)
  opponentActivatedBuff?: string | null // AGC: Opponent's proc'd buff
  yourFrostUsed?: boolean // AGC: Track if Frost was used by player
  opponentFrostUsed?: boolean // AGC: Track if Frost was used by opponent
  yourBreakdown?: DamageBreakdown // AGC: Damage calculation details
  opponentBreakdown?: DamageBreakdown // AGC: Opponent damage details
  yourBuffStatus?: BuffStatusEntry[]
  opponentBuffStatus?: BuffStatusEntry[]
  log?: any
}

export const InteractiveTournamentBattle: React.FC<InteractiveTournamentBattleProps> = ({
  battleId,
  tournamentId,
  playerAddress,
  opponentAddress,
  opponentLevel,
  socket,
  selectedBuffs, // AGC: CRITICAL - Passed directly as prop, no stale state issue
  selectedPotion: propSelectedPotion = null, // AGC: Potion token ID escrowed for this battle
  onBattleEnd,
  onBackToMenu
}) => {
  // AGC: Get player stats for XP/Level display
  const { playerStats } = usePlayerProgression(playerAddress)
  // AGC: Get battle history hook to save tournament battles
  const { addBattleRecord } = usePlayerHistory(playerAddress)

  // AGC: Battle state
  const [currentTurn, setCurrentTurn] = useState(1)
  const [yourEnergy, setYourEnergy] = useState(100)
  const [opponentEnergy, setOpponentEnergy] = useState(100)
  const [turnTimeLeft, setTurnTimeLeft] = useState(60)

  // AGC: Turn input state
  const origamiOptions = useMemo(() => ['rock', 'paper', 'scissors'] as Choice[], [])
  const elementOptions = useMemo(() => ['water', 'fire', 'ice', 'wind'] as Element[], [])
  const defaultChoiceIndex = 1
  const defaultElementIndex = 1

  const [selectedChoice, setSelectedChoice] = useState<Choice>(origamiOptions[defaultChoiceIndex])
  const [selectedElement, setSelectedElement] = useState<Element>(elementOptions[defaultElementIndex])
  const [_, setChoiceCarouselIndex] = useState(defaultChoiceIndex)
  const [__, setElementCarouselIndex] = useState(defaultElementIndex)
  const [turnSubmitted, setTurnSubmitted] = useState(false)
  
  // AGC: Potion selection state (same as InteractiveP2PBattle)
  const [potionUsed, setPotionUsed] = useState(false)
  const [equippedPotion, setEquippedPotion] = useState<{ tokenId: number; potionType: number; rarity: number; magnitude: number } | null>(null)
  const [selectedPotionForTurn, setSelectedPotionForTurn] = useState<number | null>(null)
  
  // AGC: Load potion metadata ONCE from propSelectedPotion (the potion player BROUGHT to battle)
  useEffect(() => {
    const loadPotionMetadata = async () => {
      if (propSelectedPotion === null) {
        return // AGC: No potion brought to battle
      }
      
      if (equippedPotion !== null && equippedPotion.tokenId === propSelectedPotion) {
        return // AGC: Already loaded
      }
      
      try {
        const { CONTRACT_ADDRESSES, CLASSIC_POTION_1155_ABI } = await import('../../hooks/useContract')
        const rpcUrl = import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org'
        const provider = new ethers.JsonRpcProvider(rpcUrl)
        
        if (CONTRACT_ADDRESSES.CLASSIC_POTION_1155 === '0x0000000000000000000000000000000000000000') {
          console.warn('⚠️ Classic Potion contract not configured')
          return
        }
        
        const contract = new ethers.Contract(
          CONTRACT_ADDRESSES.CLASSIC_POTION_1155,
          CLASSIC_POTION_1155_ABI,
          provider
        )
        
        const metadata = await contract.getPotionMetadata(propSelectedPotion)
        setEquippedPotion({
          tokenId: propSelectedPotion,
          potionType: Number(metadata[0]),
          rarity: Number(metadata[1]),
          magnitude: Number(metadata[2])
        })
        console.log('✅ [Tournament] Loaded potion metadata for BattleCenter:', { potionType: metadata[0], rarity: metadata[1], magnitude: metadata[2] })
      } catch (error) {
        console.warn('⚠️ [Tournament] Failed to load potion metadata:', error)
      }
    }
    
    loadPotionMetadata()
  }, [propSelectedPotion, equippedPotion])

  const submitButtonRef = useRef<HTMLButtonElement | null>(null)
  const [highlightSubmitButton, setHighlightSubmitButton] = useState(false)

  // AGC: Battle log
  const [battleLog, setBattleLog] = useState<TurnResult[]>([])
  const [waitingForOpponent, setWaitingForOpponent] = useState(false)
  const [turnSnapshot, setTurnSnapshot] = useState<BattleTurnSnapshot | null>(null)

  // AGC: Battle end state
  const [battleEnded, setBattleEnded] = useState(false)
  const [winner, setWinner] = useState<string | null>(null)
  const [isResolvingOnChain, setIsResolvingOnChain] = useState(false)
  const [showProcessingOverlay, setShowProcessingOverlay] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const [matchResult, setMatchResult] = useState<'win' | 'loss' | 'draw' | null>(null)

  // AGC: CRITICAL - selectedBuffs is passed directly as prop, no stale state issue
  const submitTurnRef = useRef<((choice: Choice, elem: Element) => void) | undefined>(undefined)
  
  const submitTurn = useCallback((choice: Choice, elem: Element) => {
    if (turnSubmitted) return

    if (!socket) {
      console.warn('AGC: Ignoring submitTurn - socket not connected')
      return
    }

    console.log('🏆 Tournament: Submitting turn:', { choice, elem, buffs: selectedBuffs, potion: selectedPotionForTurn })

    unlockAudioPlayback()

    // AGC: Mark potion as used after submission if it was selected for this turn
    if (selectedPotionForTurn !== null && equippedPotion && selectedPotionForTurn === equippedPotion.tokenId) {
      setPotionUsed(true)
    }

    socket.emit('submit_turn', {
      battleId,
      playerAddress,
      turn: { 
        choice, 
        element: elem, 
        buffs: selectedBuffs, // AGC: Use selectedBuffs directly from props
        potions: selectedPotionForTurn !== null ? [selectedPotionForTurn] : [] // AGC: Include potion token ID if selected for this turn
      }
    })

    setTurnSubmitted(true)
    setWaitingForOpponent(true)
  }, [socket, battleId, playerAddress, selectedBuffs, selectedPotionForTurn, equippedPotion, turnSubmitted])

  submitTurnRef.current = submitTurn

  const handleAutoSubmit = useCallback(() => {
    const randomChoiceIndex = Math.floor(Math.random() * origamiOptions.length)
    const randomElementIndex = Math.floor(Math.random() * elementOptions.length)
    const randomChoice = origamiOptions[randomChoiceIndex]
    const randomElement = elementOptions[randomElementIndex]

    setChoiceCarouselIndex(randomChoiceIndex)
    setElementCarouselIndex(randomElementIndex)
    setSelectedChoice(randomChoice)
    setSelectedElement(randomElement)

    if (submitTurnRef.current) {
      submitTurnRef.current(randomChoice, randomElement)
    }
  }, [origamiOptions, elementOptions])

  // AGC: Turn timer countdown
  useEffect(() => {
    if (turnSubmitted || battleEnded) return

    const timer = setInterval(() => {
      setTurnTimeLeft((prev) => {
        if (prev <= 1) {
          if (!turnSubmitted) {
            handleAutoSubmit()
          }
          return 60
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [turnSubmitted, battleEnded, handleAutoSubmit])

  useEffect(() => {
    setShowProcessingOverlay(false)
  }, [battleId])

  // AGC: Helper - scroll to and highlight the submit button when the player needs to act
  const focusSubmitButton = useCallback(() => {
    if (!submitButtonRef.current) {
      return
    }
    setHighlightSubmitButton(true)
  }, [])

  // AGC: Automatically clear the button highlight after a short delay
  useEffect(() => {
    if (!highlightSubmitButton) return
    const timeoutId = setTimeout(() => setHighlightSubmitButton(false), 3000)
    return () => clearTimeout(timeoutId)
  }, [highlightSubmitButton])

  // AGC: Helper functions - defined before socket useEffect
  const getChoiceImage = (choice: Choice | string) => {
    const normalizedChoice = choice?.toLowerCase()
    switch (normalizedChoice) {
      case 'rock': return '/rock.png'
      case 'paper': return '/paper.png'
      case 'scissors': return '/scissor.png'
      default: return '/rock.png'
    }
  }

  const getElementEmoji = (elem: Element | string) => {
    const normalizedElem = elem?.toLowerCase()
    switch (normalizedElem) {
      case 'water': return '💧'
      case 'fire': return '🔥'
      case 'ice': return '❄️'
      case 'wind': return '💨'
      default: return ''
    }
  }

  const buffMeta: Record<number, { icon: string; name: string }> = useMemo(
    () => ({
      0: { icon: '🛡️', name: 'SHIELD' },
      1: { icon: '🗡️', name: 'PIERCE' },
      2: { icon: '⚡', name: 'HASTE' },
      3: { icon: '❄️', name: 'FROST' }
    }),
    []
  )

  const mapBuffStatusEntries = useCallback(
    (
      statusEntries?: BuffStatusEntry[],
      fallbackIds?: number[],
      activatedBuffName?: string | null,
      frostUsed?: boolean
    ): BuffStatusEntry[] => {
      if (statusEntries && statusEntries.length > 0) {
        return statusEntries.map((entry) => {
          const id = typeof entry.id === 'number' ? entry.id : fallbackIds?.find((buffId) => buffMeta[buffId]?.name === entry.name)
          return {
            id: id ?? -1,
            name: entry.name,
            status: entry.status
          }
        })
      }

      if (!fallbackIds || fallbackIds.length === 0) {
        return []
      }

      const normalizedActivated = activatedBuffName ? activatedBuffName.toString().toUpperCase() : null

      return fallbackIds.map((buffId) => {
        const meta = buffMeta[buffId]
        const name = meta?.name || `BUFF_${buffId}`

        let status: BuffStatus = 'failed'
        if (normalizedActivated === name) {
          status = 'activated'
        } else if (name === 'FROST' && frostUsed) {
          status = 'disabled'
        }

        return {
          id: buffId,
          name,
          status
        }
      })
    },
    [buffMeta]
  )

  const toP2PTurnSnapshot = useCallback(
    (turn: TurnResult): BattleTurnSnapshot => {
      const yourEntries = mapBuffStatusEntries(turn.yourBuffStatus, turn.yourBuffs, turn.yourActivatedBuff, turn.yourFrostUsed)
      const opponentEntries = mapBuffStatusEntries(
        turn.opponentBuffStatus,
        turn.opponentBuffs,
        turn.opponentActivatedBuff,
        turn.opponentFrostUsed
      )

      const chipify = (entries: BuffStatusEntry[]) =>
        entries.map((entry) => {
          const meta = entry.id >= 0 ? buffMeta[entry.id] : undefined
          const baseLabel = entry.name || meta?.name || (entry.id >= 0 ? `BUFF_${entry.id}` : 'BUFF')
          const statusValue = entry.status?.toLowerCase()
          let status: 'active' | 'ready' | 'used' = 'ready'
          if (statusValue === 'activated') {
            status = 'active'
          } else if (statusValue === 'disabled') {
            status = 'used'
          }
          return { label: baseLabel, status }
        })

      return {
        turnNumber: turn.turnNumber,
        yourChoice: turn.yourChoice,
        opponentChoice: turn.opponentChoice,
        yourElement: turn.yourElement,
        opponentElement: turn.opponentElement,
        yourDamage: turn.yourDamage,
        opponentDamage: turn.opponentDamage,
        yourEnergy: turn.yourEnergy,
        opponentEnergy: turn.opponentEnergy,
        yourBuffs: chipify(yourEntries),
        opponentBuffs: chipify(opponentEntries),
        yourBreakdown: turn.yourBreakdown,
        opponentBreakdown: turn.opponentBreakdown,
      }
    },
    [buffMeta, mapBuffStatusEntries]
  )

  const handleBattleResolution = useCallback(async (winnerAddress: string) => {
    if (isResolvingOnChain) return

    setIsResolvingOnChain(true)

    try {
      if (!window.ethereum) throw new Error('No wallet detected')

      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()

      const p2pManagerAddress = import.meta.env.VITE_P2P_MANAGER_ADDRESS
      const buff1155Address = import.meta.env.VITE_BUFF1155_ADDRESS
      const classicPotion1155Address = import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS

      const p2pManager = new ethers.Contract(
        p2pManagerAddress,
        ['function resolveBattle(bytes32 battleId, address winner, address loser, address buff1155Address, address potion1155Address) external'],
        signer
      )

      const loserAddress = winnerAddress.toLowerCase() === playerAddress.toLowerCase()
        ? opponentAddress
        : playerAddress

      console.log('🏆 Tournament: Calling resolveBattle on-chain...')
      console.log(`  Battle ID: ${battleId}`)
      console.log(`  Tournament ID: ${tournamentId}`)
      console.log(`  Winner: ${winnerAddress}`)
      console.log(`  Loser: ${loserAddress}`)
      console.log(`  Buff1155: ${buff1155Address}`)
      console.log(`  Potion1155: ${classicPotion1155Address || 'none'}`)
      console.log(`  Final Turn: ${currentTurn}`)

      const tx = await p2pManager.resolveBattle(
        battleId, 
        winnerAddress, 
        loserAddress, 
        buff1155Address,
        classicPotion1155Address || '0x0000000000000000000000000000000000000000'
      )
      console.log(`[WAIT] Tournament resolution TX: ${tx.hash}`)
      await tx.wait()
      console.log(`[OK] Tournament battle resolved on-chain!`)

      return tx.hash
    } catch (error: any) {
      console.error(`[ERROR] Tournament resolution error:`, error)

      if (error.message?.includes('already resolved') ||
          error.data?.message?.includes('already resolved') ||
          error.reason?.includes('already resolved')) {
        console.log(`[OK] Tournament battle already resolved by other player`)
        return null
      }

      console.log(`[WARN] Tournament resolution attempt failed, but battle likely already resolved`)
      return null
    } finally {
      setIsResolvingOnChain(false)
    }
  }, [isResolvingOnChain, battleId, tournamentId, playerAddress, opponentAddress, currentTurn])

  // AGC: Listen for WebSocket events
  useEffect(() => {
    if (!socket) {
      console.warn('AGC: Socket not ready, skipping listener registration')
      return
    }

    socket.on('turn_result', (data: TurnResult) => {
      console.log('🏆 Tournament: Turn result received:', data)

      setBattleLog((prev) => [...prev, data])
      setYourEnergy(data.yourEnergy)
      setOpponentEnergy(data.opponentEnergy)
      setWaitingForOpponent(false)
      setTurnSnapshot(toP2PTurnSnapshot(data))

      // AGC: Play punch sound when turn ends
      playSoundSafe('/sounds/punch.mp3', 0.5)

      if (data.yourEnergy > 0 && data.opponentEnergy > 0) {
        setTurnSubmitted(false)
        setSelectedChoice(origamiOptions[defaultChoiceIndex])
        setSelectedElement(elementOptions[defaultElementIndex])
        setChoiceCarouselIndex(defaultChoiceIndex)
        setElementCarouselIndex(defaultElementIndex)
        setTurnTimeLeft(60)
        setSelectedPotionForTurn(null) // AGC: Reset potion selection for next turn
      } else {
        setShowProcessingOverlay(true)
      }
    })

    socket.on('next_turn', (data) => {
      setCurrentTurn(data.turnNumber)
    })

    socket.on('battle_end', async (data) => {
      console.log('🏆 Tournament: Battle ended:', data)
      setBattleEnded(true)
      setWinner(data.winner)
      setTurnTimeLeft(0)
      setTurnSnapshot(null)
      setShowProcessingOverlay(false)

      // AGC: Calculate battle statistics for history
      const finalTurnCount = data.turns || data.battleLog?.length || currentTurn
      const isDraw = data.isDraw || false
      // AGC: FIX - Handle null/undefined winner in draw scenarios
      const isPlayerWinner = !isDraw && data.winner && typeof data.winner === 'string'
        ? data.winner.toLowerCase() === playerAddress.toLowerCase()
        : false
      
      // AGC: Determine outcome for history
      const outcome: 'WIN' | 'LOSS' | 'DRAW' = isDraw ? 'DRAW' : isPlayerWinner ? 'WIN' : 'LOSS'
      const xpOutcome: 'WIN' | 'DRAW' | 'LOSE' = outcome === 'DRAW' ? 'DRAW' : isPlayerWinner ? 'WIN' : 'LOSE'
      
      // AGC: Calculate experience gained (tournament battles use PVP XP rates)
      const playerLevel = playerStats?.level || 1
      const experienceGained = GameEngine.calculateExperienceGain(
        xpOutcome,
        opponentLevel,
        playerLevel,
        'PVP' // AGC: Tournament battles are PvP
      )

      // AGC: Check if it's a draw
      if (isDraw) {
        console.log('🏆 Tournament: Battle ended in a DRAW!')
        setShowProcessingOverlay(false)
        setIsResolvingOnChain(false)
        setMatchResult('draw')
        setShowResultModal(true)
        
        // AGC: Report draw to tournament (resolve on-chain)
        const resolutionTxHash = await handleBattleResolution(data.winner || playerAddress)
        
        // AGC: Save battle record to player history
        addBattleRecord({
          outcome: 'DRAW',
          turns: finalTurnCount,
          isKnockout: false, // AGC: Draws can't be knockouts
          opponentLevel,
          playerLevel,
          experienceGained,
          tokensWon: 0, // AGC: Tournament battles don't award tokens
          tokensLost: 0,
          opponentAddress,
          battleType: 'PVP',
          battleId,
          ...(resolutionTxHash && { resolutionTxHash })
        })
        
        onBattleEnd(false, finalTurnCount, true, resolutionTxHash)
        
        // AGC: Auto-close modal after 3 seconds
        setTimeout(() => {
          setShowResultModal(false)
        }, 3000)
        return
      }

      // AGC: Not a draw - determine winner
      setMatchResult(isPlayerWinner ? 'win' : 'loss')
      setShowResultModal(true)

      // AGC: Play winner/loser sound
      if (isPlayerWinner) {
        playSoundSafe('/sounds/youwin.mp3', 0.6)
      } else {
        playSoundSafe('/sounds/gameover.mp3', 0.6)
      }

      setShowProcessingOverlay(false)
      setIsResolvingOnChain(isPlayerWinner) // AGC: Only winner resolves on-chain
      
      // AGC: Resolve on-chain (only winner calls this)
      let resolutionTxHash: string | null = null
      if (isPlayerWinner) {
        resolutionTxHash = await handleBattleResolution(data.winner)
      }

      // AGC: Save battle record to player history
      addBattleRecord({
        outcome,
        turns: finalTurnCount,
        isKnockout: isPlayerWinner && finalTurnCount <= 3, // AGC: KO if won in 3 or fewer turns
        opponentLevel,
        playerLevel,
        experienceGained,
        tokensWon: 0, // AGC: Tournament battles don't award tokens
        tokensLost: 0,
        opponentAddress,
        battleType: 'PVP',
        battleId,
        ...(resolutionTxHash && { resolutionTxHash })
      })

      // AGC: Report result to tournament
      onBattleEnd(isPlayerWinner, finalTurnCount, false, resolutionTxHash)
      
      // AGC: Auto-close modal after 3 seconds
      setTimeout(() => {
        setShowResultModal(false)
      }, 3000)
    })

    return () => {
      socket.off('turn_result')
      socket.off('next_turn')
      socket.off('battle_end')
    }
  }, [socket, battleLog.length, currentTurn, onBattleEnd, playerAddress, toP2PTurnSnapshot, handleBattleResolution, origamiOptions, elementOptions, defaultChoiceIndex, tournamentId, opponentLevel, opponentAddress, battleId, playerStats, addBattleRecord])

  const handleSubmitTurn = () => {
    submitTurn(selectedChoice, selectedElement)
  }

  if (!socket) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-700 rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-white mb-2">Connecting to battle server...</h2>
            <p className="text-slate-300">
              Waiting for real-time socket connection. This can take a few seconds inside Farcaster mini apps.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <main className="pt-[72px] pb-[88px] px-3 min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-6xl mx-auto">
        <BattleTurnModal
          isOpen={!!turnSnapshot && !battleEnded}
          turn={turnSnapshot}
          onClose={() => setTurnSnapshot(null)}
          turnTimeLeft={turnTimeLeft}
          contextLabel="🏆 Tournament Battle Turn"
        />

        {showProcessingOverlay && (
          <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 pointer-events-none"></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                role="status"
                aria-live="assertive"
                className="w-full max-w-md rounded-2xl border border-blue-400/70 bg-slate-900/95 text-center shadow-[0_20px_60px_rgba(0,0,0,0.65)] px-6 py-8"
              >
                <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-4 border-blue-300 border-t-transparent"></div>
                <h3 className="text-2xl font-bold text-blue-100 mb-2">Processing Tournament Battle…</h3>
                <p className="text-slate-100/80 text-sm leading-relaxed">
                  Finalizing tournament match settlement on Base. Please keep this screen open until the result is processed.
                </p>
              </div>
            </div>
          </>
        )}

        {/* AGC: Match Result Modal - Shows win/loss/draw after battle ends */}
        {showResultModal && matchResult && (
          <>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div
                role="alert"
                aria-live="assertive"
                className={`w-full max-w-md rounded-2xl border-2 text-center shadow-[0_20px_60px_rgba(0,0,0,0.8)] px-8 py-12 transform transition-all duration-300 ${
                  matchResult === 'win'
                    ? 'border-emerald-400/70 bg-gradient-to-br from-emerald-900/95 to-green-800/95'
                    : matchResult === 'loss'
                    ? 'border-red-400/70 bg-gradient-to-br from-red-900/95 to-rose-800/95'
                    : 'border-yellow-400/70 bg-gradient-to-br from-yellow-900/95 to-amber-800/95'
                }`}
              >
                {matchResult === 'win' && (
                  <>
                    <div className="text-6xl mb-4 animate-bounce">🏆</div>
                    <h2 className="text-4xl font-bold text-emerald-100 mb-2 animate-pulse">VICTORY!</h2>
                    <p className="text-emerald-200/90 text-lg">You won this match!</p>
                  </>
                )}
                {matchResult === 'loss' && (
                  <>
                    <div className="text-6xl mb-4 animate-bounce">💔</div>
                    <h2 className="text-4xl font-bold text-red-100 mb-2 animate-pulse">DEFEAT</h2>
                    <p className="text-red-200/90 text-lg">You lost this match</p>
                  </>
                )}
                {matchResult === 'draw' && (
                  <>
                    <div className="text-6xl mb-4 animate-bounce">🤝</div>
                    <h2 className="text-4xl font-bold text-yellow-100 mb-2 animate-pulse">DRAW</h2>
                    <p className="text-yellow-200/90 text-lg">The match ended in a tie</p>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* AGC: BattleTop - Clean 3-column battle header */}
        <BattleTop
          playerAddress={playerAddress}
          playerStats={playerStats as BattleTopPlayerStats}
          playerEnergy={yourEnergy}
          playerBuffs={selectedBuffs}
          opponentInfo={{
            label: 'Opponent',
            level: opponentLevel,
            energy: opponentEnergy,
            address: opponentAddress
          } as OpponentInfo}
          opponentBuffs={[]}
          currentTurn={currentTurn}
          turnTimeLeft={turnTimeLeft}
          turnSubmitted={turnSubmitted}
          waitingForOpponent={waitingForOpponent}
          battleEnded={battleEnded}
          winner={winner}
          onBackToMenu={onBackToMenu}
        />

        {/* AGC: BattleCenter - Reusable battle center component */}
        <BattleCenter
          turnSubmitted={turnSubmitted}
          waitingForOpponent={waitingForOpponent}
          battleEnded={battleEnded}
          turnTimeLeft={turnTimeLeft}
          selectedChoice={selectedChoice}
          selectedElement={selectedElement}
          onChoiceSelect={(choice) => {
            setSelectedChoice(choice)
            const index = origamiOptions.indexOf(choice)
            if (index >= 0) {
              setChoiceCarouselIndex(index)
            }
            focusSubmitButton()
          }}
          onElementSelect={(element) => {
            setSelectedElement(element)
            const index = elementOptions.indexOf(element)
            if (index >= 0) {
              setElementCarouselIndex(index)
            }
            focusSubmitButton()
          }}
          onSubmitTurn={handleSubmitTurn}
          getChoiceImage={getChoiceImage}
          getElementEmoji={getElementEmoji}
          origamiOptions={origamiOptions}
          elementOptions={elementOptions}
          equippedPotion={equippedPotion}
          selectedPotion={selectedPotionForTurn}
          onPotionSelect={(potionId) => setSelectedPotionForTurn(potionId)}
          potionUsed={potionUsed}
        />

        {/* AGC: BattleLog - Reusable battle log component */}
        <BattleLog
          battleLog={battleLog as BattleLogTurnResult[]}
          getChoiceImage={getChoiceImage}
          getElementEmoji={getElementEmoji}
          opponentLabel="Opponent"
        />
      </div>
    </main>
  )
}

